import {
  useState,
  useRef,
  useEffect,
  createContext,
  useContext,
  useCallback,
} from "react";
import { useRestoreFocus } from "../../hooks/useFocusTrap";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useLearnCtx } from "./LearnPage";
import { processText } from "../../utils/learnUtils";
import {
  deleteCommentReply as deleteCommentReplyAction,
  addCommentReply,
  addCommentReplyAI,
  upDownVoteComment,
} from "../../actions/comment";
import {
  ADD_NEW_COMMENT_REPLY,
  SIGNAL_START_STOPWATCH,
} from "../../constants/actionConstants";
import "./CommentsPanel.css";

/* ─── Internal context for comment mutations ─────────────── */
const ActionsCtx = createContext(null);
function useActions() {
  return useContext(ActionsCtx);
}

/**
 * Reddit-style comment thread panel (right panel).
 * Opens when commentsFrameId is set.
 * @param {{ onClose: () => void }} props
 */
export default function CommentsPanel({ onClose }) {
  useRestoreFocus();
  const closeRef = useRef(null);
  useEffect(() => {
    closeRef.current?.focus();
  }, []);
  const { commentsMap, commentsFrameId, commentsItemId } = useLearnCtx();
  const allFrameComments = commentsFrameId
    ? (commentsMap[commentsFrameId] ?? [])
    : [];
  const baseComments = commentsItemId
    ? allFrameComments.filter((c) => c._id === commentsItemId)
    : allFrameComments;

  const profile = JSON.parse(localStorage.getItem("profile"));
  const currentUserId = profile?.result?._id ?? null;
  const currentUserName = profile?.result?.name ?? "Anonymous";
  const isAdmin = !!profile?.result?.admin;

  const dispatch = useDispatch();
  const { id: courseId } = useParams();
  const upDownVotes = useSelector(
    (state) => state.ProgressReducer.upDownVotes ?? [],
  );

  /* ── Mutation state ── */
  const [openEditId, setOpenEditId] = useState(null);
  const [editDrafts, setEditDrafts] = useState({}); // id → draft string
  const [savedEdits, setSavedEdits] = useState({}); // id → saved string
  const [localDeleted, setLocalDeleted] = useState(new Set());
  const [openReplyId, setOpenReplyId] = useState(null);
  const [replyDraft, setReplyDraft] = useState("");
  // { parentChildIndices, rootCommentId, currentChildCount } — captured when reply form opens
  const [replyContext, setReplyContext] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null); // { commentId, childIndices, rootCommentId, isRoot, isLocal }

  const startEdit = useCallback(
    (id, currentText) => {
      setOpenEditId(id);
      setEditDrafts((prev) => ({
        ...prev,
        [id]: savedEdits[id] ?? currentText,
      }));
      setOpenReplyId(null);
    },
    [savedEdits],
  );

  const cancelEdit = useCallback(() => setOpenEditId(null), []);

  const saveEdit = useCallback(
    (id) => {
      setSavedEdits((prev) => ({ ...prev, [id]: editDrafts[id] ?? "" }));
      setOpenEditId(null);
    },
    [editDrafts],
  );

  const requestDelete = useCallback(
    (commentId, childIndices, rootCommentId, isRoot, isLocal) => {
      setPendingDelete({
        commentId,
        childIndices,
        rootCommentId,
        isRoot,
        isLocal,
      });
      if (openEditId === commentId) setOpenEditId(null);
      if (openReplyId === commentId) setOpenReplyId(null);
    },
    [openEditId, openReplyId],
  );

  const confirmDelete = useCallback(() => {
    if (!pendingDelete) return;
    const { commentId, childIndices, rootCommentId, isRoot, isLocal } =
      pendingDelete;
    if (isLocal) {
      setLocalDeleted((prev) => new Set([...prev, commentId]));
    } else {
      dispatch(
        deleteCommentReplyAction(
          courseId,
          commentsFrameId,
          childIndices,
          rootCommentId,
        ),
      );
    }
    setPendingDelete(null);
    if (isRoot) onClose();
  }, [pendingDelete, dispatch, courseId, commentsFrameId, onClose]);

  const cancelDelete = useCallback(() => setPendingDelete(null), []);

  const openReply = useCallback(
    (id, parentChildIndices, rootCommentId, currentChildCount) => {
      setOpenReplyId((prev) => (prev === id ? null : id));
      setReplyContext({ parentChildIndices, rootCommentId, currentChildCount });
      setReplyDraft("");
      setOpenEditId(null);
    },
    [],
  );

  const submitReply = useCallback(
    (parentId) => {
      const text = replyDraft.trim();
      if (!text || !replyContext) return;

      const { parentChildIndices, rootCommentId, currentChildCount } =
        replyContext;

      const aiTagged = text.includes("@AI") || text.includes("@ai");
      const cleanText = text
        .replaceAll("@AI ", "")
        .replaceAll("@ai ", "")
        .replaceAll("@AI", "")
        .replaceAll("@ai", "")
        .trim();

      const now = Date.now();

      const reply = aiTagged
        ? {
            _id: crypto.randomUUID(),
            author: currentUserName,
            author_id: currentUserId ?? "local",
            date: now,
            upvotes: 0,
            contents: cleanText,
            children: [
              {
                _id: crypto.randomUUID(),
                author: "AI",
                author_id: currentUserId ?? "local",
                date: now,
                upvotes: 0,
                contents: "AI response pending",
                children: [],
                edited: false,
                deleted: false,
              },
            ],
            edited: false,
            deleted: false,
          }
        : {
            _id: crypto.randomUUID(),
            author: currentUserName,
            author_id: currentUserId ?? "local",
            date: now,
            upvotes: 0,
            contents: cleanText,
            children: [],
            edited: false,
            deleted: false,
          };

      dispatch({
        type: ADD_NEW_COMMENT_REPLY,
        payload: {
          reply,
          content_id: commentsFrameId,
          comment_id: rootCommentId,
          child_indices: parentChildIndices,
        },
      });

      if (aiTagged) {
        // Path from root to the AI placeholder: [...parentChildIndices, newReplyIndex, 0]
        const childIndicesReply = [...parentChildIndices, currentChildCount, 0];
        dispatch(
          addCommentReplyAI(
            courseId,
            0,
            commentsFrameId,
            reply,
            parentChildIndices,
            rootCommentId,
            "",
            cleanText,
            childIndicesReply,
          ),
        );
      } else {
        dispatch(
          addCommentReply(
            courseId,
            commentsFrameId,
            reply,
            parentChildIndices,
            rootCommentId,
          ),
        );
      }

      setOpenReplyId(null);
      setReplyDraft("");
    },
    [
      replyDraft,
      replyContext,
      dispatch,
      courseId,
      commentsFrameId,
      currentUserId,
      currentUserName,
    ],
  );

  const actions = {
    openEditId,
    editDrafts,
    savedEdits,
    localDeleted,
    openReplyId,
    replyDraft,
    startEdit,
    cancelEdit,
    saveEdit,
    requestDelete,
    openReply,
    submitReply,
    setEditDrafts,
    setReplyDraft,
    currentUserId,
    isAdmin,
    upDownVotes,
    courseId,
    commentsFrameId,
  };

  return (
    <ActionsCtx.Provider value={actions}>
      <div className="cp">
        <div className="cp__header">
          <span className="cp__header-title">Discussion</span>
          <button
            ref={closeRef}
            className="cp__close"
            onClick={onClose}
            aria-label="Close discussion"
          >
            ✕
          </button>
        </div>
        {pendingDelete && (
          <div className="cp__confirm-delete">
            <p className="cp__confirm-msg">
              Delete this comment and all its replies? This cannot be undone.
            </p>
            <div className="cp__confirm-actions">
              <button
                className="ct-node__action-btn ct-node__action-btn--cancel"
                onClick={cancelDelete}
              >
                Cancel
              </button>
              <button
                className="ct-node__action-btn cp__confirm-delete-btn"
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        )}
        <div className="cp__body">
          {baseComments.length === 0 ? (
            <p className="cp__empty">No discussions for this section yet.</p>
          ) : (
            <ul className="cp__thread">
              {baseComments.map((c) => (
                <CommentThread
                  key={c._id}
                  comment={c}
                  depth={0}
                  childIndices={[]}
                  rootCommentId={c._id}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </ActionsCtx.Provider>
  );
}

/* ─── MathJax streaming helpers ─────────────────────────── */

// Returns the index after the last "safe" character — one where all $…$, $$…$$,
// **…**, and *…* spans are balanced. Text up to this point can be passed to
// processText and appended to the DOM without waiting for more content.
//
// Key insight: bold/italic can't cross newlines ([^*\n]+ pattern), so a \n
// always resets markdown state — any unmatched * on a completed line is literal.
function findSafeSplitPoint(text) {
  let lastSafePos = 0;
  let i = 0;
  let inBold = false;
  let inItalic = false;

  while (i < text.length) {
    const ch = text[i];

    if (ch === "\n") {
      inBold = false;
      inItalic = false;
      lastSafePos = i + 1;
      i++;
      continue;
    }

    if (ch === "$") {
      if (i + 1 < text.length && text[i + 1] === "$") {
        // Display math $$…$$ (may span lines)
        const closeIdx = text.indexOf("$$", i + 2);
        if (closeIdx === -1) break; // unclosed — stop
        if (!inBold && !inItalic) lastSafePos = closeIdx + 2;
        i = closeIdx + 2;
        inBold = false;
        inItalic = false;
      } else {
        // Inline math $…$ (no newlines inside)
        let j = i + 1;
        let found = false;
        while (j < text.length && text[j] !== "\n") {
          if (text[j] === "$") {
            if (j + 1 < text.length && text[j + 1] === "$") break; // hit $$
            if (!inBold && !inItalic) lastSafePos = j + 1;
            i = j + 1;
            found = true;
            break;
          }
          j++;
        }
        if (!found) break; // unclosed inline math — stop
      }
      continue;
    }

    if (ch === "*") {
      if (i + 1 < text.length && text[i + 1] === "*") {
        // ** bold
        if (inBold) {
          inBold = false;
          lastSafePos = i + 2;
        } else {
          inBold = true;
        }
        i += 2;
      } else if (inItalic) {
        // closing single *
        inItalic = false;
        lastSafePos = i + 1;
        i++;
      } else if (!inBold) {
        // opening single *
        inItalic = true;
        i++;
      } else {
        // * inside **bold** — treat as content
        i++;
      }
      continue;
    }

    if (!inBold && !inItalic) lastSafePos = i + 1;
    i++;
  }

  return lastSafePos;
}

/* ─── Recursive comment node ─────────────────────────────── */
function CommentThread({
  comment,
  depth,
  collapseControl = null,
  childIndices = [],
  rootCommentId = null,
}) {
  const {
    openEditId,
    editDrafts,
    savedEdits,
    localDeleted,
    openReplyId,
    replyDraft,
    startEdit,
    cancelEdit,
    saveEdit,
    requestDelete,
    openReply,
    submitReply,
    setEditDrafts,
    setReplyDraft,
    currentUserId,
    isAdmin,
    upDownVotes,
    courseId,
    commentsFrameId,
  } = useActions();

  const dispatch = useDispatch();
  const effectiveRootId = rootCommentId ?? comment._id;

  const [collapsed, setCollapsed] = useState(false);
  const [votes, setVotes] = useState(comment.upvotes ?? 0);
  const storedVote = upDownVotes.find((v) => v.id === comment._id);
  const [userVote, setUserVote] = useState(
    storedVote ? (storedVote.updown ? 1 : -1) : 0,
  );
  const stableRef = useRef(null);
  const tailRef = useRef(null);
  const stableTextRef = useRef("");

  const isLocallyDeleted = localDeleted.has(comment._id);
  const isEditing = openEditId === comment._id;
  const isReplying = openReplyId === comment._id;
  const isAI = comment.author === "AI";
  const isLocal = comment.author_id === "local";
  const isAIPending =
    isAI &&
    savedEdits[comment._id] === undefined &&
    comment.contents === "AI response pending";

  const displayContent = savedEdits[comment._id] ?? comment.contents ?? "";
  const wasEdited = comment.edited || savedEdits[comment._id] !== undefined;

  const allChildren = (comment.children ?? []).filter((c) => !c.deleted);
  const hasChildren = allChildren.length > 0;

  // Incrementally typesets streaming content without re-rendering already-typeset
  // equations. stableRef grows by appending; MathJax fires only on each new chunk.
  // tailRef holds trailing text whose equations aren't yet complete (updated raw).
  useEffect(() => {
    const stableEl = stableRef.current;
    const tailEl = tailRef.current;
    if (!stableEl || !tailEl) return;

    const splitPoint = findSafeSplitPoint(displayContent);
    const newStableText = displayContent.slice(0, splitPoint);
    const tailText = displayContent.slice(splitPoint);

    tailEl.innerHTML = processText(tailText);

    if (newStableText === stableTextRef.current) return;

    if (newStableText.startsWith(stableTextRef.current)) {
      const addedHtml = processText(
        newStableText.slice(stableTextRef.current.length),
      );
      const span = document.createElement("span");
      span.innerHTML = addedHtml;
      stableEl.appendChild(span);
      stableTextRef.current = newStableText;
      if (window.MathJax?.typesetPromise) {
        setTimeout(() => {
          if (!span.isConnected) return;
          window.MathJax.typesetPromise([span]).catch(() => {});
        }, 0);
      }
    } else {
      stableEl.innerHTML = processText(newStableText);
      stableTextRef.current = newStableText;
      if (window.MathJax?.typesetPromise) {
        setTimeout(() => {
          if (!stableEl.isConnected) return;
          if (window.MathJax.typesetClear)
            window.MathJax.typesetClear([stableEl]);
          window.MathJax.typesetPromise([stableEl]).catch(() => {});
        }, 0);
      }
    }
  }, [displayContent]);

  const dispatchVote = (increment, updown, updatedVotes) => {
    const details = {
      author_id: currentUserId,
      comment_id: comment._id,
      root_comment_id: effectiveRootId,
      increment,
      content_id: commentsFrameId,
      updown,
      course_id: courseId,
      child_indices: childIndices,
    };
    dispatch(upDownVoteComment(courseId, details, updatedVotes));
    dispatch({ type: SIGNAL_START_STOPWATCH, payload: Math.random() });
  };

  const handleUpvote = () => {
    let increment;
    let updatedVotes = structuredClone(upDownVotes);
    if (userVote === 1) {
      // toggle off upvote
      setVotes((v) => v - 1);
      setUserVote(0);
      increment = -1;
      updatedVotes = updatedVotes.filter((v) => v.id !== comment._id);
    } else if (userVote === -1) {
      // switch from downvote to upvote
      setVotes((v) => v + 2);
      setUserVote(1);
      increment = 2;
      const idx = updatedVotes.findIndex((v) => v.id === comment._id);
      if (idx >= 0) updatedVotes[idx].updown = true;
      else updatedVotes.push({ id: comment._id, updown: true });
    } else {
      // new upvote
      setVotes((v) => v + 1);
      setUserVote(1);
      increment = 1;
      updatedVotes.push({ id: comment._id, updown: true });
    }
    dispatchVote(increment, true, updatedVotes);
  };

  const handleDownvote = () => {
    let increment;
    let updatedVotes = structuredClone(upDownVotes);
    if (userVote === -1) {
      // toggle off downvote
      setVotes((v) => v + 1);
      setUserVote(0);
      increment = 1;
      updatedVotes = updatedVotes.filter((v) => v.id !== comment._id);
    } else if (userVote === 1) {
      // switch from upvote to downvote
      setVotes((v) => v - 2);
      setUserVote(-1);
      increment = -2;
      const idx = updatedVotes.findIndex((v) => v.id === comment._id);
      if (idx >= 0) updatedVotes[idx].updown = false;
      else updatedVotes.push({ id: comment._id, updown: false });
    } else {
      // new downvote
      setVotes((v) => v - 1);
      setUserVote(-1);
      increment = -1;
      updatedVotes.push({ id: comment._id, updown: false });
    }
    dispatchVote(increment, false, updatedVotes);
  };

  if (isLocallyDeleted && !hasChildren) return null;

  return (
    <li className={`ct-node ct-node--depth-${Math.min(depth, 4)}`}>
      <div className="ct-node__main">
        {/* Collapse line */}
        {hasChildren ? (
          <button
            className="ct-node__thread-line"
            onClick={() => setCollapsed((v) => !v)}
            aria-label={collapsed ? "Expand" : "Collapse"}
            title={collapsed ? "Expand replies" : "Collapse replies"}
          />
        ) : (
          <div className="ct-node__thread-spacer" />
        )}

        <div className="ct-node__content">
          {/* Meta */}
          <div className="ct-node__meta">
            {collapseControl && (
              <button
                className="ct-node__collapse-caret"
                onClick={collapseControl.onCollapse}
                title="Collapse replies"
              >
                ▼
              </button>
            )}
            <span
              className={`ct-node__author ${isAI ? "ct-node__author--ai" : ""} ${isLocal ? "ct-node__author--local" : ""}`}
            >
              {isAI ? "AI" : (comment.author ?? "Anonymous")}
            </span>
            {comment.date && (
              <span className="ct-node__date">
                {new Date(comment.date).toLocaleDateString(undefined, {
                  month: "short",
                  day: "numeric",
                })}
              </span>
            )}
            {wasEdited && !isLocallyDeleted && (
              <span className="ct-node__edited">(edited)</span>
            )}
          </div>

          {/* Body or edit form */}
          {isLocallyDeleted ? (
            <p className="ct-node__deleted">[deleted]</p>
          ) : isEditing ? (
            <div className="ct-node__edit-form">
              <textarea
                className="ct-node__edit-textarea"
                value={editDrafts[comment._id] ?? displayContent}
                onChange={(e) =>
                  setEditDrafts((prev) => ({
                    ...prev,
                    [comment._id]: e.target.value,
                  }))
                }
                rows={4}
                autoFocus
              />
              <div className="ct-node__edit-actions">
                <button
                  className="ct-node__action-btn ct-node__action-btn--cancel"
                  onClick={cancelEdit}
                >
                  Cancel
                </button>
                <button
                  className="ct-node__action-btn ct-node__action-btn--save"
                  onClick={() => saveEdit(comment._id)}
                  disabled={!(editDrafts[comment._id] ?? displayContent).trim()}
                >
                  Save
                </button>
              </div>
            </div>
          ) : isAIPending ? (
            <span className="ct-node__ai-pending" aria-label="AI is responding">
              <span />
              <span />
              <span />
            </span>
          ) : (
            <div className="ct-node__body">
              <span ref={stableRef} />
              <span ref={tailRef} />
            </div>
          )}

          {/* Actions row: votes + reply/edit/delete */}
          {!isLocallyDeleted && !isEditing && !isAIPending && (
            <div className="ct-node__actions-row">
              {/* Votes */}
              <div className="ct-node__votes">
                <button
                  className={`ct-node__vote-btn ${userVote === 1 ? "ct-node__vote-btn--up" : ""}`}
                  onClick={handleUpvote}
                  aria-label="Upvote"
                  title="Upvote"
                >
                  ▲
                </button>
                <span className="ct-node__vote-count">{votes}</span>
                <button
                  className={`ct-node__vote-btn ${userVote === -1 ? "ct-node__vote-btn--down" : ""}`}
                  onClick={handleDownvote}
                  aria-label="Downvote"
                  title="Downvote"
                >
                  ▼
                </button>
              </div>

              {/* Separator */}
              <span className="ct-node__actions-sep" />

              {/* Reply */}
              <button
                className={`ct-node__inline-btn ${isReplying ? "ct-node__inline-btn--active" : ""}`}
                onClick={() =>
                  openReply(
                    comment._id,
                    childIndices,
                    effectiveRootId,
                    allChildren.length,
                  )
                }
                title="Reply"
              >
                <ReplyIcon /> Reply
              </button>

              <div className="ct-node__actions-right">
                {(isLocal ||
                  isAdmin ||
                  currentUserId === comment.author_id) && (
                  <button
                    className="ct-node__inline-btn"
                    onClick={() => startEdit(comment._id, displayContent)}
                    title="Edit"
                  >
                    <EditIcon />
                  </button>
                )}

                {(isLocal ||
                  isAdmin ||
                  currentUserId === comment.author_id) && (
                  <button
                    className="ct-node__inline-btn ct-node__inline-btn--danger"
                    onClick={() =>
                      requestDelete(
                        comment._id,
                        childIndices,
                        effectiveRootId,
                        depth === 0,
                        isLocal,
                      )
                    }
                    title="Delete"
                  >
                    <DeleteIcon />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Inline reply form */}
          {isReplying && (
            <div className="ct-node__reply-form">
              <textarea
                className="ct-node__reply-textarea"
                value={replyDraft}
                onChange={(e) => setReplyDraft(e.target.value)}
                placeholder="Write a reply… Use $…$.  Tag @AI to get a response from AI."
                rows={3}
                autoFocus
              />
              <div className="ct-node__edit-actions">
                <button
                  className="ct-node__action-btn ct-node__action-btn--cancel"
                  onClick={() => openReply(null)}
                >
                  Cancel
                </button>
                <button
                  className="ct-node__action-btn ct-node__action-btn--save"
                  onClick={() => submitReply(comment._id)}
                  disabled={!replyDraft.trim()}
                >
                  Reply
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Children */}
      {hasChildren &&
        (collapsed ? (
          <button
            className="ct-node__replies-btn"
            onClick={() => setCollapsed(false)}
          >
            ▶ {allChildren.length} repl{allChildren.length > 1 ? "ies" : "y"}
          </button>
        ) : (
          <ul className="ct-node__children">
            {allChildren.map((child, i) => (
              <CommentThread
                key={child._id ?? i}
                comment={child}
                depth={depth + 1}
                childIndices={[...childIndices, i]}
                rootCommentId={effectiveRootId}
                {...(i === 0
                  ? {
                      collapseControl: { onCollapse: () => setCollapsed(true) },
                    }
                  : {})}
              />
            ))}
          </ul>
        ))}
    </li>
  );
}

/* ─── Icon components ──────────────────────────────────────── */
function ReplyIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M1 4.5h7a3 3 0 0 1 0 6H6"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3.5 2L1 4.5 3.5 7"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 11 11"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M7.5 1.5l2 2L3 10H1V8l6.5-6.5z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function DeleteIcon() {
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 11 11"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M2 3h7M4 3V2h3v1M4.5 5v3M6.5 5v3M3 3l.5 6h4L8 3"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
